import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, useCurrency, API } from "../App";
import { useGoogleLogin } from "@react-oauth/google";
import { toast } from "sonner";
import { roleHome } from "../utils/roleHome";
import { xhrJson } from "../utils/xhrJson";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const logoSrc = (url) => url ? (url.startsWith("http") ? url : `${BACKEND_URL}${url}`) : "/logo-white.png";

/**
 * Detect WebView / Capacitor environment.
 */
function isWebView() {
  if (typeof window === "undefined") return false;
  // Capacitor native shell
  if (window.Capacitor?.isNativePlatform?.()) return true;
  if (window.Capacitor) return true;
  const ua = navigator.userAgent || "";
  // Android WebView
  if (/wv/.test(ua)) return true;
  if (/Android/.test(ua) && /Version\/[\d.]+/.test(ua) && !/Chrome\/[\d.]+ Mobile Safari/.test(ua)) return true;
  // iOS WKWebView (no Safari token)
  if (/iPhone|iPad|iPod/.test(ua) && !/Safari/.test(ua)) return true;
  // Facebook / Instagram / other in-app browsers
  if (/FBAN|FBAV|Instagram|Line\/|Twitter/.test(ua)) return true;
  return false;
}

const SLIDES = [
  "/slide1.jpg",
  "/slide2.jpg",
  "/slide3.jpg",
  "/slide4.jpg",
  "/slide5.jpg",
];

export default function LandingPage() {
  const { user, loading, googleLogin } = useAuth();
  const { logoUrl, homeLogoUrl } = useCurrency();
  const landingLogo = homeLogoUrl || logoUrl;
  const navigate = useNavigate();
  const [loggingIn, setLoggingIn] = useState(false);
  const [current, setCurrent] = useState(0);
  const [regEnabled, setRegEnabled] = useState(true);
  // Warm-return hint persisted by AuthProvider on successful login. `null`
  // for first-time visitors → pill reads "Continue with Google".
  const [returning, setReturning] = useState(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("sm_returning_user");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.first_name) setReturning(parsed);
      }
    } catch (_) { /* ignore */ }
  }, []);

  const forgetReturning = () => {
    try { localStorage.removeItem("sm_returning_user"); } catch (_) {}
    setReturning(null);
  };

  useEffect(() => {
    if (!loading && user) {
      navigate(roleHome(user.role), { replace: true });
    }
  }, [user, loading, navigate]);

  // Fetch registration status
  useEffect(() => {
    fetch(`${API}/settings/registration`)
      .then(r => r.ok ? r.json() : { registration_enabled: true })
      .then(d => setRegEnabled(d.registration_enabled))
      .catch(() => {});
  }, []);

  const goTo = useCallback((idx) => {
    setCurrent(idx);
  }, []);

  // Auto-advance every 4s
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((p) => (p + 1) % SLIDES.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // iOS Safari/WebKit (iOS 17+) raises a TypeError "Load failed" when fetch
  // is called in the same microtask as a popup-window close. Deferring by
  // ~400ms lets WebKit restore the parent window's first-party network
  // context. We also retry once on the same error pattern as a safety net.
  const isIOSWebKit = (() => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    return /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  })();

  const postGoogleToken = async (accessToken) => {
    const doFetch = () => fetch(`${API}/auth/google`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ access_token: accessToken }),
    });
    if (isIOSWebKit) {
      // Give WebKit a moment to restore the parent's network context
      await new Promise((r) => setTimeout(r, 400));
    }
    try {
      return await doFetch();
    } catch (e) {
      // Single retry — iOS Safari often succeeds on a second attempt once
      // the popup teardown is complete.
      if (isIOSWebKit) {
        await new Promise((r) => setTimeout(r, 600));
        return await doFetch();
      }
      throw e;
    }
  };

  const login = useGoogleLogin({
    flow: "implicit",
    onSuccess: async (tokenResponse) => {
      setLoggingIn(true);
      try {
        if (!tokenResponse?.access_token) {
          throw new Error("Google did not return an access token");
        }
        let res;
        try {
          res = await postGoogleToken(tokenResponse.access_token);
        } catch (netErr) {
          throw new Error(`Network: ${netErr?.message || "request blocked"}`);
        }
        if (res.ok) {
          const userData = await res.json();
          googleLogin(null, userData);
          toast.success(`Welcome, ${userData.name || "User"}!`);
          navigate(roleHome(userData.role), { replace: true });
        } else {
          let detail = "";
          try {
            const e = await res.clone().json();
            detail = e.detail || JSON.stringify(e);
          } catch {
            try { detail = (await res.text()).slice(0, 200); } catch { /* ignore */ }
          }
          throw new Error(`HTTP ${res.status}${detail ? ` — ${detail}` : ""}`);
        }
      } catch (e) {
        console.error("Login error:", e);
        toast.error(`Login failed: ${e?.message || "unknown error"}`);
      }
      setLoggingIn(false);
    },
    onError: (err) => {
      console.error("Google sign-in error:", err);
      toast.error(`Google sign-in failed${err?.error ? `: ${err.error}` : ""}. Please try again.`);
    },
  });

  // WebView-aware login handler — uses polling (no deep link needed)
  const handleLogin = () => {
    // iOS Safari & iOS Chrome (both WebKit) have a known issue where fetch()
    // called immediately after a popup-window close throws TypeError "Load failed".
    // The implicit popup flow from @react-oauth/google triggers exactly that
    // failure mode. Route iOS WebKit through the server-side redirect flow
    // (full-page navigation → Google → backend callback redirects back to
    // /?session_token=… which the useEffect at the top of this file picks up).
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (isIOSWebKit && !isWebView()) {
      setLoggingIn(true);
      window.location.href = `${API}/auth/google/start?source=web`;
      return;
    }
    if (isWebView()) {
      const authCode = 'ac_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
      const startUrl = `${API}/auth/google/start?source=app&auth_code=${authCode}`;

      setLoggingIn(true);

      // Open system browser for OAuth
      if (window.NativeBridge?.openUrl) {
        window.NativeBridge.openUrl(startUrl);
      } else {
        window.open(startUrl, '_blank');
      }

      let resolved = false;
      let cookieReady = false;

      const doPoll = async () => {
        if (resolved) return;
        try {
          const res = await fetch(`${API}/auth/poll-token`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ auth_code: authCode }),
          });
          if (!res.ok || resolved) return;
          const data = await res.json();
          if (data.status === "ok") {
            cookieReady = true;
            // Only navigate if page is visible right now
            if (document.visibilityState === "visible") {
              resolved = true;
              clearInterval(pollInterval);
              document.removeEventListener("visibilitychange", onVisible);
              window.location.href = window.location.origin;
            }
            // If hidden, cookie is set but don't navigate — onVisible will handle it
          }
        } catch (_) {}
      };

      // Poll every 2s — runs in background too (sets cookie even if navigation blocked)
      let attempts = 0;
      const pollInterval = setInterval(() => {
        attempts++;
        if (attempts > 90) {
          clearInterval(pollInterval);
          document.removeEventListener("visibilitychange", onVisible);
          setLoggingIn(false);
          toast.error("Login timed out. Please try again.");
          return;
        }
        doPoll();
      }, 2000);

      // When app returns to foreground
      const onVisible = () => {
        if (document.visibilityState === "visible" && !resolved) {
          if (cookieReady) {
            // Cookie was set in background — just reload
            resolved = true;
            clearInterval(pollInterval);
            document.removeEventListener("visibilitychange", onVisible);
            window.location.href = window.location.origin;
          } else {
            // Cookie not ready yet — poll immediately
            doPoll();
            setTimeout(doPoll, 800);
          }
        }
      };
      document.addEventListener("visibilitychange", onVisible);
    } else {
      login();
    }
  };

  // Handle session_token from URL (callback from server-side OAuth / deep link)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("session_token");
    const authError = params.get("auth_error");
    if (authError) {
      toast.error(authError === "token_exchange_failed" ? "Login failed. Please try again." : authError);
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    if (!token) return;
    let cancelled = false;
    (async () => {
      setLoggingIn(true);
      // iOS Safari is fragile right after a cross-site navigation. The cookie
      // is *already* set by /api/auth/google/callback's Set-Cookie header on
      // the 302 redirect — try a simple GET /auth/me first (much more reliable
      // than POST on iOS WebKit). Only fall back to POST exchange-token if
      // the cookie didn't stick (e.g. ITP rejected it).
      if (isIOSWebKit) {
        await new Promise((r) => setTimeout(r, 400));
      }
      let meRes;
      try {
        meRes = await fetch(`${API}/auth/me`, { credentials: "include" });
      } catch {
        meRes = null;
      }
      if (!cancelled && meRes?.ok) {
        try {
          const userData = await meRes.json();
          googleLogin(null, userData);
          toast.success(`Welcome, ${userData.name || "User"}!`);
          window.history.replaceState({}, "", window.location.pathname);
          try { window.Capacitor?.Plugins?.Browser?.close(); } catch (_) {}
          navigate(roleHome(userData.role), { replace: true });
          setLoggingIn(false);
          return;
        } catch (_) {
          // fall through to exchange-token fallback
        }
      }
      // Fallback: POST exchange-token with the URL session_token (with retry).
      let result;
      try {
        try {
          result = await xhrJson("POST", `${API}/auth/exchange-token`, { session_token: token });
        } catch (firstErr) {
          if (isIOSWebKit) {
            await new Promise((r) => setTimeout(r, 600));
            result = await xhrJson("POST", `${API}/auth/exchange-token`, { session_token: token });
          } else {
            throw firstErr;
          }
        }
      } catch (netErr) {
        if (!cancelled) {
          console.error("Session exchange network error:", netErr);
          toast.error(`Login failed: Network: ${netErr?.message || "request blocked"}`);
          window.history.replaceState({}, "", window.location.pathname);
          setLoggingIn(false);
        }
        return;
      }
      if (cancelled) return;
      if (result.ok) {
        googleLogin(null, result.data);
        toast.success(`Welcome, ${result.data.name || "User"}!`);
        window.history.replaceState({}, "", window.location.pathname);
        try { window.Capacitor?.Plugins?.Browser?.close(); } catch (_) {}
        navigate(roleHome(result.data.role), { replace: true });
      } else {
        const detail = result.data?.detail || JSON.stringify(result.data || {});
        console.error("Session exchange non-OK:", result.status, detail);
        toast.error(`Login failed: HTTP ${result.status}${detail ? ` — ${detail}` : ""}`);
        window.history.replaceState({}, "", window.location.pathname);
      }
      if (!cancelled) setLoggingIn(false);
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Unified Google sign-in pill — used across mobile + desktop. Personalises
  // to "Continue as {firstName}" (with the user's Google avatar) on return
  // visits, based on the `sm_returning_user` localStorage hint.
  // `size="lg"` → wider, taller pill for desktop; default → mobile compact.
  const GoogleButton = ({ testid, size = "md" }) => {
    if (loggingIn) {
      return (
        <div className="flex items-center justify-center gap-3 text-white py-4">
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          <span className="text-sm">Signing in...</span>
        </div>
      );
    }
    const label = returning?.first_name ? `Continue as ${returning.first_name}` : "Continue with Google";
    const sizeCls = size === "lg"
      ? "w-[290px] xl:w-[325px] h-12 text-sm gap-3"
      : "w-[260px] sm:w-[300px] h-12 text-sm gap-3";
    const iconCls = size === "lg" ? "w-5 h-5" : "w-5 h-5";
    const avatarCls = size === "lg" ? "w-6 h-6" : "w-6 h-6";
    return (
      <button
        onClick={() => handleLogin()}
        className={`${sizeCls} bg-white hover:bg-gray-50 active:bg-gray-100 rounded-full flex items-center justify-center shadow-lg shadow-black/25 transition-all duration-300`}
        data-testid={testid}
      >
        {returning?.picture ? (
          <img
            src={returning.picture}
            alt=""
            referrerPolicy="no-referrer"
            className={`${avatarCls} rounded-full object-cover`}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <svg className={iconCls} viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
        )}
        <span className="text-gray-800 font-semibold tracking-wide">{label}</span>
      </button>
    );
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#0e3d2a]" data-testid="login-screen">
      {/* Mobile: full-bleed hero image + login CTA over bottom green wave */}
      <div className="absolute inset-0 lg:hidden bg-white">
        <img
          src="/login-hero.jpg"
          alt="Super Morsel — delighting tastebuds with thoughtful ingredients"
          className="w-full h-full object-cover object-center"
          fetchpriority="high"
        />
      </div>

      {/* Desktop: single full-bleed hero image (landscape). Button + link
          overlay onto the dark-green wave section at the bottom of the
          image. Composition matches the mobile treatment horizontally.
          — `100dvh` respects the browser chrome so the image doesn't
             overflow behind the address bar.
          — Image renders at its natural 3:2 aspect ratio (`max-h-[100dvh]`),
             wrapped in a relatively-positioned container so the CTA is
             pinned to the IMAGE's bottom-left, not the viewport's. */}
      <div className="hidden lg:flex min-h-[100dvh] items-center justify-center bg-[#0e3d2a] overflow-hidden">
        <div className="relative">
          <img
            src="/login-hero-desktop.jpg"
            alt="Super Morsel — delighting tastebuds with thoughtful ingredients"
            className="max-h-[100dvh] w-auto h-auto block"
            fetchpriority="high"
          />
          <div className="absolute bottom-6 xl:bottom-10 right-6 xl:right-10 flex flex-col items-end gap-3">
            <GoogleButton testid="google-signin-button-desktop" size="lg" />
            {returning?.first_name && !loggingIn && (
              <button
                type="button"
                onClick={forgetReturning}
                className="text-white/80 hover:text-white text-[11px] tracking-wide transition-colors underline-offset-2 hover:underline pr-2"
                data-testid="not-you-btn-desktop"
              >
                Not {returning.first_name}?
              </button>
            )}
            <a
              href="/whats-super-morsel.html"
              className="text-white/85 hover:text-white text-xs tracking-wide transition-colors duration-300 pr-2"
              data-testid="ask-demo-btn-desktop"
            >
              What's Super Morsel
            </a>
            {!regEnabled && (
              <p className="text-white/70 text-[11px] pr-2 text-right">
                New registrations are currently closed. Existing users can still sign in.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Mobile content layer — sits inside the dark-green wave section
          at the bottom of the hero image. Logo + tagline come from the
          image itself, so we only render the CTA + secondary link here.
          `env(safe-area-inset-bottom)` keeps the "What's Super Morsel"
          link clear of the OS gesture bar / navigation bar on Android
          and iOS. */}
      <div
        className="relative z-10 min-h-screen lg:hidden flex flex-col items-center justify-end px-6 pt-10"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 6rem)" }}
      >
        <div className="w-full flex flex-col items-center gap-3">
          <GoogleButton testid="google-signin-button" />

          {returning?.first_name && !loggingIn && (
            <button
              type="button"
              onClick={forgetReturning}
              className="text-white/70 hover:text-white text-[11px] tracking-wide transition-colors underline-offset-2 hover:underline"
              data-testid="not-you-btn"
            >
              Not {returning.first_name}?
            </button>
          )}

          {/* What's Super Morsel — sits right below the Connect button */}
          <a
            href="/whats-super-morsel.html"
            className="block text-center text-white/85 hover:text-white text-xs tracking-wide transition-colors duration-300"
            data-testid="ask-demo-btn"
          >
            What's Super Morsel
          </a>

          {/* Registration closed notice */}
          {!regEnabled && (
            <p className="text-center text-white/70 text-[11px]" data-testid="reg-closed-notice">
              New registrations are currently closed. Existing users can still sign in.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
